/**
 * PatientChartQuickLinks — chart toolbar in the clinical workflow order:
 * Part 1 Subjective → Part 2 Objective → SOAP/DAP note → Treatment plan →
 * Progression → 30-day outcomes → GP handoff (+ massage, share, print, new visit).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Printer, ClipboardList, FileSignature, FileText, FileSearch, Activity, Sparkles, Stethoscope, TrendingDown, X } from 'lucide-react';

export default function PatientChartQuickLinks({
  patient_id, settings,
  printOpen, setPrintOpen, printSel, setPrintSel, doPrint,
  openBriefing, briefingLoading,
  onNewVisit,
}) {
  const gpHandoff = async () => {
    toast.info('Generating GP handoff PDF…');
    try {
      const r = await api.get(`/patients/${patient_id}/gp-handoff.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (!window.open(url, '_blank')) {
        const a = document.createElement('a');
        a.href = url; a.download = `gp-handoff-${patient_id.slice(0, 8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch {
      toast.error('Could not generate the GP handoff PDF.');
    }
  };

  return (
    <div className="flex gap-2 print:hidden flex-wrap">
      <button onClick={() => setPrintOpen(true)}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="chart-print">
        <Printer className="w-4 h-4"/> Print
      </button>
      {printOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setPrintOpen(false)}>
          <div className="w-full max-w-sm rounded-xl border border-soft surface shadow-xl p-5 relative"
               onClick={e => e.stopPropagation()} data-testid="print-menu">
            <button className="absolute top-3 right-3 text-fg-muted hover:text-fg" onClick={() => setPrintOpen(false)}>
              <X className="w-4 h-4"/>
            </button>
            <div className="font-heading font-semibold mb-1 flex items-center gap-2"><Printer className="w-4 h-4 text-brand"/> Print chart</div>
            <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-2">Sections to include</div>
            <SelToggle checked={printSel.visits} onChange={v => setPrintSel({ ...printSel, visits: v })} testid="ps-visits">Visit timeline (SOAP)</SelToggle>
            <SelToggle checked={printSel.discharge} onChange={v => setPrintSel({ ...printSel, discharge: v })} testid="ps-discharge">Discharge</SelToggle>
            <SelToggle checked={printSel.referral} onChange={v => setPrintSel({ ...printSel, referral: v })} testid="ps-referral">Referral</SelToggle>
            <SelToggle checked={printSel.emergency} onChange={v => setPrintSel({ ...printSel, emergency: v })} testid="ps-emergency">Emergency contact</SelToggle>
            <SelToggle checked={printSel.interview} onChange={v => setPrintSel({ ...printSel, interview: v })} testid="ps-interview">Part 1 — Subjective examination</SelToggle>
            <SelToggle checked={printSel.objective} onChange={v => setPrintSel({ ...printSel, objective: v })} testid="ps-objective">Part 2 — Objective examination</SelToggle>
            <SelToggle checked={printSel.treatment} onChange={v => setPrintSel({ ...printSel, treatment: v })} testid="ps-treatment">Treatment plan</SelToggle>
            <button onClick={doPrint}
                    className="w-full mt-3 text-sm font-semibold text-white rounded-lg py-2 bg-[#2563EB] hover:bg-[#1D4ED8] transition-colors"
                    data-testid="print-confirm">
              Print selected
            </button>
          </div>
        </div>
      )}
      <button onClick={openBriefing} disabled={briefingLoading}
              className="btn-secondary inline-flex items-center gap-2"
              title="One-page summary: upcoming reason + AI flags + last 3 SOAP notes + active plan"
              data-testid="chart-briefing">
        <FileSearch className="w-4 h-4"/> {briefingLoading ? 'Loading…' : 'Pre-visit briefing'}
      </button>

      {/* Clinical workflow order (iter 144, per clinic owner request) */}
      <Link to={`/admin/patients/${patient_id}/interview`} className="btn-secondary inline-flex items-center gap-2" data-testid="chart-interview-link">
        <ClipboardList className="w-4 h-4"/> Part 1 · Subjective examination
      </Link>
      <Link to={`/admin/patients/${patient_id}/objective-assessment`} className="btn-secondary inline-flex items-center gap-2" data-testid="chart-obj-link">
        <ClipboardList className="w-4 h-4"/> Part 2 · Objective examination
      </Link>
      <Link to={`/admin/patients/${patient_id}/note`} className="btn-secondary inline-flex items-center gap-2" data-testid="chart-note-link">
        <FileText className="w-4 h-4"/> SOAP / DAP note
      </Link>
      <Link to={`/admin/patients/${patient_id}/treatment-plan`} className="btn-secondary inline-flex items-center gap-2" data-testid="chart-treatment-link">
        <FileSignature className="w-4 h-4"/> Treatment plan
      </Link>
      {(settings.feature_layered_body_chart !== false || settings.feature_multidisc_timeline !== false) && (
        <Link to={`/admin/patients/${patient_id}/progression`} className="btn-secondary inline-flex items-center gap-2" data-testid="chart-progression-link">
          <Activity className="w-4 h-4"/> Progression
        </Link>
      )}
      <Link to={`/admin/patients/${patient_id}/outcomes-30day`} className="btn-secondary inline-flex items-center gap-2" data-testid="chart-outcomes-30day">
        <TrendingDown className="w-4 h-4"/> 30-day outcomes
      </Link>
      <button onClick={gpHandoff}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="chart-gp-handoff">
        <Stethoscope className="w-4 h-4"/> GP handoff
      </button>

      {(settings.service_disciplines || []).includes('massage_therapy') && (settings.feature_massage_intake !== false) && (
        <Link to={`/admin/patients/${patient_id}/massage-assessment`} className="btn-secondary inline-flex items-center gap-2" data-testid="chart-massage-link">
          <ClipboardList className="w-4 h-4"/> Massage assessment
        </Link>
      )}
      <Link to={`/admin/patients/${patient_id}/share-progress`} className="btn-secondary inline-flex items-center gap-2" data-testid="chart-share-progress-link">
        <Sparkles className="w-4 h-4"/> Share progress
      </Link>
      <button onClick={onNewVisit} className="btn-primary inline-flex items-center gap-2" data-testid="new-visit-btn">
        <Plus className="w-4 h-4"/> New visit
      </button>
    </div>
  );
}

function SelToggle({ checked, onChange, testid, children }) {
  return (
    <label className="flex items-center gap-2 py-1 text-sm cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}
             className="accent-brand" data-testid={testid}/>
      <span>{children}</span>
    </label>
  );
}
